import { PettyCash, PettyCashExpense } from '../../../types';
import { apiService } from '../../../services/api.service';

export interface SelectOption { value: string; label: string }

export const employeesQO = {
  sort: [{ field: 'name', order: 'asc' as const }],
  pagination: { pageSize: 500 },
};

export const branchesQO = {
  sort: [{ field: 'name', order: 'asc' as const }],
  pagination: { pageSize: 200 },
};

const branchLabel = (b: any) =>
  `${b.name}${b.type === 'vessel' ? ' (Vessel)' : b.type === 'office' ? ' (HQ)' : ''}`;

/** Branch names (HQs + vessels); keeps a legacy saved value selectable */
export function buildBranchNameOptions(branches: any[], savedValue?: string): SelectOption[] {
  const opts = branches.filter((b) => b.name).map((b) => ({ value: b.name, label: branchLabel(b) }));
  if (savedValue && !opts.some((o) => o.value === savedValue)) {
    opts.unshift({ value: savedValue, label: savedValue });
  }
  return opts;
}

/** Expense "Class" = the real categories from the Categories module */
export function buildClassOptions(categories: any[], expenses?: PettyCashExpense[]): SelectOption[] {
  const opts = categories
    .filter((c) => c.name && c.isActive !== false)
    .map((c) => ({ value: c.name, label: c.name }));
  (expenses || []).forEach((e) => {
    if (e.class && !opts.some((o) => o.value === e.class)) {
      opts.unshift({ value: e.class, label: e.class });
    }
  });
  return opts;
}

/** Expense "الفرع" options; keeps legacy saved values selectable */
export function buildExpenseBranchOptions(branches: any[], expenses?: PettyCashExpense[]): SelectOption[] {
  const opts = branches.filter((b) => b.name).map((b) => ({ value: b.name, label: branchLabel(b) }));
  (expenses || []).forEach((e) => {
    if (e.category && !opts.some((o) => o.value === e.category)) {
      opts.unshift({ value: e.category, label: e.category });
    }
  });
  return opts;
}

function employeeIdSet(employees: any[], employeeId: string): { emp: any; ids: Set<string> } {
  const emp: any = employees.find(
    (e: any) => e.documentId === employeeId || String(e.id) === String(employeeId)
  );
  const ids = new Set(
    [employeeId, emp?.documentId, emp?.id != null ? String(emp.id) : null].filter(Boolean) as string[]
  );
  return { emp, ids };
}

/** The employee's most recent petty cash record, if any */
export function latestRecordFor(
  records: PettyCash[],
  employees: any[],
  employeeId: string
): PettyCash | undefined {
  const { emp, ids } = employeeIdSet(employees, employeeId);
  return records
    .filter((r) => ids.has(String(r.employeeId)) ||
      Boolean(emp?.name && r.employeeName && r.employeeName === emp.name))
    .sort((a, b) =>
      (new Date(b.date).getTime() - new Date(a.date).getTime()) ||
      (new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    )[0];
}

/**
 * The balance is cumulative per employee: a new record starts from the
 * final balance of the employee's most recent petty cash record.
 * Stored totals can be stale on old records, so derive from the expense rows.
 */
export function recordFinalBalance(record?: PettyCash): number {
  if (!record) return 0;
  const expenses = (record.expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  return (Number(record.previousBalance) || 0) + (Number(record.totalReceived) || 0) - expenses;
}

/**
 * Fill previousBalance/totalReceived for a new record. The backend endpoint
 * GET /api/petty-cash/employee-balance is the source of truth; the local
 * computation is only a fallback until that endpoint exists.
 */
export async function applyEmployeeBalance(opts: {
  employeeId: string;
  records: PettyCash[];
  transactions: any[];
  employees: any[];
  setValue: (field: 'previousBalance' | 'totalReceived', value: number) => void;
}): Promise<void> {
  const { employeeId, records, transactions, employees, setValue } = opts;
  try {
    const res: any = await apiService.get(
      `/petty-cash/employee-balance?employee=${encodeURIComponent(employeeId)}`
    );
    const data = res?.data ?? res;
    if (data && (data.previousBalance !== undefined || data.receivedSinceLastRecord !== undefined)) {
      setValue('previousBalance', Number(data.previousBalance) || 0);
      setValue('totalReceived', Number(data.receivedSinceLastRecord) || 0);
      return;
    }
  } catch { /* endpoint not available yet — fall back to local computation */ }
  const latest = latestRecordFor(records, employees, employeeId);
  setValue('previousBalance', recordFinalBalance(latest));
  setValue('totalReceived', receivedAfter(transactions, employees, employeeId, latest?.createdAt));
}

/**
 * Cash handed to the employee via out-transactions after their last petty
 * cash statement — it becomes the "Total Received" of the new statement.
 */
export function receivedAfter(
  transactions: any[],
  employees: any[],
  employeeId: string,
  afterIso?: string
): number {
  const { ids } = employeeIdSet(employees, employeeId);
  const after = afterIso ? new Date(afterIso).getTime() : 0;
  return transactions
    .filter((t) => t.type === 'out' && t.recipientType === 'employee' &&
      ids.has(String(t.recipientId)))
    .filter((t) => new Date(t.createdAt || t.date).getTime() > after)
    .reduce((s, t) => s + (Number(t.amount) || 0), 0);
}
